<template>
  <v-card
    elevation="3"
    class="client-card rounded-lg mx-auto my-1 pa-3"
    :class="cardClasses"
    @click="showClient"
    :ripple="hasProject"
  >
    <div class="d-flex align-start">
      <!-- Status Indicator -->
      <div class="status-indicator mr-3 mt-1">
        <v-icon :color="statusColor" size="20">mdi-circle</v-icon>
        <v-icon color="white" size="10" class="status-indicator-inner">
          mdi-circle
        </v-icon>
      </div>

      <!-- Main Content -->
      <div class="flex-grow-1 content-wrapper">
        <!-- Header with ID and Menu -->
        <div class="d-flex justify-space-between align-start mb-1">
          <div class="font-weight-bold">
            {{ client.project ? client.project.metagenomicId : "--" }}
          </div>
          <v-menu offset-y left :close-on-content-click="true">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                class="menu-btn mt-n1"
                v-bind="attrs"
                v-on="on"
                @click.stop
              >
                <v-icon size="18">mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item
                v-for="(item, index) in menu"
                :key="index"
                @click="selection(item.value)"
                class="menu-item"
              >
                <v-list-item-title class="text-caption">
                  {{ item.text }}
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>

        <!-- Project Status -->
        <div class="project-status text-caption font-weight-medium mb-2">
          {{ projectStatusText }}
        </div>

        <!-- Date and Area Information -->
        <div class="info-grid mb-2">
          <div class="info-item">
            <span class="info-label">{{ translations.created_project }}:</span>
            <span class="info-value">{{ formattedDate }}</span>
          </div>
          <div class="info-item">
            <span class="info-label primary--text">{{ translations.cultivableArea }}:</span>
            <span class="info-value primary--text"
              >{{ client.cultivableArea || '---' }} ha</span
            >
          </div>
        </div>

        <!-- Divider -->
        <v-divider class="my-2 divider-custom"></v-divider>

        <!-- Company Information -->
        <div class="company-info">
          <div class=" text-body-2 font-weight-bold mb-1">
            {{ billingCompanyName }}
          </div>
          <div class="company-secondary text-caption mb-2">
            {{ client.companyName }}
          </div>
        </div>

        <!-- Contact Information -->
        <div class="contact-info d-flex justify-space-between text-caption">
          <div class="contact-email truncate">
            {{ client.email }}
          </div>
          <div class="contact-phone text-right">
            {{ formattedPhone }}
          </div>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
/**
 * ClientCard Component
 *
 * Displays client information in a card format with project status, contact details,
 * and interactive actions. Supports project navigation and client updates.
 *
 * @component
 * @example
 * <client-card :client="clientData" />
 */

// ===== IMPORTS =====
import { mapGetters, mapMutations } from "vuex";

export default {
  name: "ClientCard",

  props: {
    /**
     * Client data object containing project, company, and contact information
     * @property {Object} client - Client data object
     * @property {Object} client.project - Project information
     * @property {string} client.project.metagenomicId - Unique project identifier
     * @property {string} client.project.status - Project status (NEW, IN_PROGRESS, FINISHED)
     * @property {string} client.project.date - Project creation date
     * @property {Object} client.billingInformation - Billing details
     * @property {string} client.billingInformation.companyName - Billing company name
     * @property {string} client.companyName - Client company name
     * @property {string} client.email - Client email address
     * @property {string} client.phone - Client phone number
     * @property {number} client.cultivableArea - Cultivable area in hectares
     * @property {string} client._id - Client unique identifier
     */
    client: {
      type: Object,
      required: true,
      validator: (value) => {
        return value && typeof value === "object";
      },
    },
  },

  // ===== COMPOSABLES & STORES =====
  computed: {
    ...mapGetters("session", ["currentTranslations"]),
  },

  // ===== REACTIVE STATE =====
  data() {
    return {
      /**
       * Context menu items array
       * @type {Array<{text: string, value: string}>}
       */
      menu: [],
    };
  },

  // ===== COMPUTED PROPERTIES =====
  computed: {
    ...mapGetters("session", ["currentTranslations"]),

    /**
     * Current translations object from store
     * @returns {Object} Translation key-value pairs
     */
    translations() {
      return this.currentTranslations;
    },

    /**
     * CSS classes for the card based on project existence
     * @returns {Object} CSS class object
     */
    cardClasses() {
      return {
        "cursor-pointer": this.hasProject,
        "cursor-not-allowed": !this.hasProject,
      };
    },

    /**
     * Determines if client has an associated project
     * @returns {boolean} True if client has a project
     */
    hasProject() {
      return !!this.client.project;
    },

    /**
     * Formatted creation date of the project
     * @returns {string} Formatted date string or placeholder
     */
    formattedDate() {
      return this.client.project
        ? this.formatDate(this.client.project.date)
        : "--/--/----";
    },

    /**
     * Billing company name with fallback to regular company name
     * @returns {string} Company name for billing
     */
    billingCompanyName() {
      return this.client.billingInformation
        ? this.client.billingInformation.companyName
        : this.client.companyName || "--";
    },

    /**
     * Formatted phone number with fallback
     * @returns {string} Phone number or placeholder
     */
    formattedPhone() {
      return this.client.phone || "--";
    },

    /**
     * Localized project status text
     * @returns {string} Translated status text
     */
    projectStatusText() {
      if (this.client.project) {
        return (
          this.translations.status_projects[this.client.project.status] ||
          this.client.project.status
        );
      } else {
        return this.translations.no_project;
      }
    },

    /**
     * Status color based on project state
     * @returns {string} CSS color value
     */
    statusColor() {
      if (this.client.project) {
        const statusColors = {
          NEW: "#38A169",
          FINISHED: "#F08300",
          IN_PROGRESS: "#38A169",
          default: "#38A169",
        };
        return statusColors[this.client.project.status] || statusColors.default;
      } else {
        return "#E53E3E";
      }
    },
  },

  // ===== LIFECYCLE HOOKS =====
  mounted() {
    this.initializeMenu();
  },

  // ===== METHODS =====
  methods: {
    ...mapMutations("alerts", ["SET_ALERT_TYPE"]),

    // ===== INITIALIZATION METHODS =====
    /**
     * Initializes context menu items
     */
    initializeMenu() {
      this.menu = [{ text: this.translations.label_update, value: "update" }];
    },

    // ===== EVENT HANDLERS =====
    /**
     * Handles context menu item selection
     * @param {string} value - Selected menu item value
     */
    selection(value) {
      switch (value) {
        case "update":
          this.navigateToClientForm();
          break;
        default:
          console.warn(`Unknown menu selection: ${value}`);
      }
    },

    /**
     * Handles card click to show client details
     * Navigates to territory view if project exists, shows error otherwise
     */
    showClient() {
      if (this.hasProject) {
        this.navigateToTerritory();
      } else {
        this.showNoProjectError();
      }
    },

    // ===== ROUTING METHODS =====
    /**
     * Navigates to client form for updating client information
     */
    navigateToClientForm() {
      this.$router.push(`/client-form/${this.client._id}`);
    },

    /**
     * Navigates to territory view and stores client data
     */
    navigateToTerritory() {
      localStorage.setItem("clientId", this.client._id);
      localStorage.setItem("client", JSON.stringify(this.client));
      this.$router.push(`/territory`);
    },

    // ===== UTILITY FUNCTIONS =====
    /**
     * Formats date string to localized format
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date string
     */
    formatDate(dateString) {
      if (!dateString) return "Fecha no disponible";

      const date = new Date(dateString);
      return date.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    },

    // ===== DATA METHODS =====
    /**
     * Shows error alert when client has no project
     */
    showNoProjectError() {
      this.SET_ALERT_TYPE({
        visible: true,
        type: "error",
        text: this.translations.advice_not_project,
        timeout: 5000,
      });
    },
  },
};
</script>

<style scoped>
.client-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  min-height: 180px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.client-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-not-allowed {
  cursor: not-allowed;
  opacity: 0.7;
}

/* Status Indicator */
.status-indicator {
  position: relative;
  flex-shrink: 0;
}

.status-indicator-inner {
  position: absolute;
  top: 5px;
  left: 5px;
  opacity: 0.5;
}

/* Main Content */
.content-wrapper {
  min-width: 0; /* For text truncation */
}

.project-id {
  color: #2d3748;
  line-height: 1.2;
}

.project-status {
  color: v-bind(statusColor);
  line-height: 1.2;
}

/* Information Grid */
.info-grid {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-label {
  color: #718096;
  font-size: 0.7rem;
}

.info-value {
  color: #4a5568;
  font-size: 0.7rem;
  font-weight: 500;
}

.highlight {
  color: #2b6cb0;
  font-weight: 600;
}

.divider-custom {
  background-color: #e2e8f0;
  opacity: 0.6;
}

/* Company Information */
.company-name {
  color: #1a202c;
  line-height: 1.3;
}

.company-secondary {
  color: #4a5568;
  line-height: 1.2;
}

/* Contact Information */
.contact-info {
  color: #4a5568;
  line-height: 1.2;
}

.truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 60%;
}

/* ===== RESPONSIVE DESIGN ===== */

/* Mobile Devices */
@media (max-width: 600px) {
  .client-card {
    margin: 2px;
    padding: 12px;
    min-height: 160px;
  }

  .project-id {
    font-size: 0.95rem;
  }

  .company-name {
    font-size: 0.85rem;
  }

  .text-caption {
    font-size: 0.7rem;
  }

  .info-label,
  .info-value {
    font-size: 0.65rem;
  }
}

/* Tablets */
@media (min-width: 601px) and (max-width: 960px) {
  .client-card {
    min-height: 170px;
  }

  .project-id {
    font-size: 1rem;
  }
}

/* Desktop */
@media (min-width: 961px) {
  .client-card {
    min-height: 180px;
  }

  .project-id {
    font-size: 1.1rem;
  }
}

/* Tablet Landscape */
@media (min-width: 960px) and (max-width: 1264px) {
  .client-card {
    min-height: 175px;
  }
}

/* Large Desktop */
@media (min-width: 1264px) {
  .client-card {
    min-height: 185px;
  }
}

/* Very Small Screens */
@media (max-width: 360px) {
  .client-card {
    padding: 8px;
    min-height: 150px;
  }

  .project-id {
    font-size: 0.9rem;
  }

  .contact-info {
    flex-direction: column;
    gap: 2px;
  }

  .truncate {
    max-width: 100%;
  }
}

/* Accessibility Improvements */
@media (prefers-reduced-motion: reduce) {
  .client-card {
    transition: none;
  }

  .client-card:hover {
    transform: none;
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .client-card {
    background-color: #1e1e1e;
    border-color: rgba(255, 255, 255, 0.1);
  }

  .project-id,
  .company-name {
    color: #e2e8f0;
  }

  .company-secondary,
  .contact-info {
    color: #a0aec0;
  }
}
</style>
